(() => {
  const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  class CtVerticalStory {
    constructor(section) {
      this.section = section;
      this.desktop = section.querySelector('[data-ct-vertical-story]');
      this.viewport = section.querySelector('[data-story-viewport]');
      this.track = section.querySelector('[data-story-track]');
      this.copyItems = Array.from(section.querySelectorAll('[data-story-copy]'));
      this.panels = Array.from(section.querySelectorAll('[data-story-panel]'));

      this.enabled = false;
      this.scrollDistance = 0;
      this.frameHeight = 0;
      this.activeStep = 0;
      this.activePanel = 0;
      this.effectiveTop = 0;
      this.raf = null;
      this.resizeTimer = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);

      this.init();

      if (typeof DESKTOP_QUERY.addEventListener === 'function') {
        DESKTOP_QUERY.addEventListener('change', this.onMediaChange);
      } else if (typeof DESKTOP_QUERY.addListener === 'function') {
        DESKTOP_QUERY.addListener(this.onMediaChange);
      }
    }

    init() {
      if (!this.desktop || !this.viewport || !this.track || !this.copyItems.length || !this.panels.length) {
        return;
      }

      if (!DESKTOP_QUERY.matches) {
        this.disable();
        return;
      }

      this.enable();
    }

    enable() {
      if (this.enabled) {
        this.setMetrics();
        this.update();
        return;
      }

      this.enabled = true;

      this.setMetrics();
      this.update();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      window.addEventListener('load', this.onResize);
    }

    disable() {
      if (!this.enabled) {
        this.resetState();
        return;
      }

      this.enabled = false;

      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('load', this.onResize);

      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }

      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = null;
      }

      this.resetState();
    }

    resetState() {
      if (this.desktop) {
        this.desktop.style.height = '';
      }

      if (this.track) {
        this.track.style.transform = '';
      }

      if (this.section) {
        this.section.style.removeProperty('--ct-vertical-story-frame-height');
        this.section.style.removeProperty('--ct-vertical-story-effective-top');
      }

      this.copyItems.forEach((item, index) => {
        item.classList.toggle('is-active', index === 0);
      });

      this.panels.forEach((item, index) => {
        item.classList.toggle('is-active', index === 0);
      });

      this.activeStep = 0;
      this.activePanel = 0;
      this.scrollDistance = 0;
      this.frameHeight = 0;
      this.effectiveTop = 0;
    }

    onMediaChange(event) {
      if (event.matches) {
        this.enable();
      } else {
        this.disable();
      }
    }

    onResize() {
      if (!this.enabled) return;

      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.setMetrics();
        this.update();
      }, 120);
    }

    onScroll() {
      if (!this.enabled || this.raf) return;

      this.raf = window.requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    }

    cssLengthToPx(value) {
      if (value == null) return 0;

      const raw = String(value).trim();
      if (!raw) return 0;

      if (raw.endsWith('px')) {
        return parseFloat(raw) || 0;
      }

      const test = document.createElement('div');
      test.style.position = 'absolute';
      test.style.visibility = 'hidden';
      test.style.pointerEvents = 'none';
      test.style.width = raw;

      document.body.appendChild(test);
      const pixels = test.getBoundingClientRect().width;
      test.remove();

      return pixels || parseFloat(raw) || 0;
    }

    readCssVar(name, element) {
      if (!element) return 0;
      const value = getComputedStyle(element).getPropertyValue(name);
      return this.cssLengthToPx(value);
    }

    getManualTopOffset() {
      const styles = getComputedStyle(this.section);
      return this.cssLengthToPx(styles.getPropertyValue('--ct-vertical-story-top'));
    }

    getHeaderOffsetFromCssVars() {
      const root = document.documentElement;
      const body = document.body;

      const values = [
        this.readCssVar('--header-bottom-position-desktop', root),
        this.readCssVar('--header-bottom-position', root),
        this.readCssVar('--header-height', root),
        this.readCssVar('--header-bottom-position-desktop', body),
        this.readCssVar('--header-bottom-position', body),
        this.readCssVar('--header-height', body)
      ];

      return Math.max(...values, 0);
    }

    getHeaderOffsetFromDom() {
      const selectors = [
        '#shopify-section-header',
        '.shopify-section-header-sticky',
        'sticky-header',
        '.header-wrapper'
      ];

      let maxBottom = 0;

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          const rect = element.getBoundingClientRect();

          if (rect.bottom <= 0) return;
          if (rect.top > 2) return;

          maxBottom = Math.max(maxBottom, rect.bottom);
        });
      });

      return maxBottom;
    }

    getEffectiveTopOffset() {
      const manualOffset = this.getManualTopOffset();
      const headerOffset = Math.max(
        this.getHeaderOffsetFromCssVars(),
        this.getHeaderOffsetFromDom()
      );

      return Math.max(headerOffset + manualOffset, 0);
    }

    syncEffectiveTop(force = false) {
      const nextTop = this.getEffectiveTopOffset();

      if (!force && Math.abs(nextTop - this.effectiveTop) < 1) {
        return false;
      }

      this.effectiveTop = nextTop;
      this.section.style.setProperty('--ct-vertical-story-effective-top', `${nextTop}px`);

      return true;
    }

    getTopOffset() {
      return this.effectiveTop || this.getEffectiveTopOffset();
    }

    setMetrics() {
      this.syncEffectiveTop(true);

      const topOffset = this.getTopOffset();
      const frameHeight = Math.max(window.innerHeight - topOffset, 320);

      this.frameHeight = frameHeight;

      this.section.style.setProperty('--ct-vertical-story-frame-height', `${frameHeight}px`);

      const trackHeight = this.track.scrollHeight;
      const viewportHeight = this.viewport.clientHeight || frameHeight;

      this.scrollDistance = Math.max(trackHeight - viewportHeight, 0);

      this.desktop.style.height = `${frameHeight + this.scrollDistance}px`;
    }

    getProgress() {
      if (!this.enabled) return 0;
      if (this.scrollDistance <= 0) return 0;

      const rect = this.desktop.getBoundingClientRect();
      const topOffset = this.getTopOffset();

      return clamp((-rect.top + topOffset) / this.scrollDistance, 0, 1);
    }

    update() {
      if (!this.enabled) return;

      const headerChanged = this.syncEffectiveTop();

      if (headerChanged) {
        this.setMetrics();
      }

      const progress = this.getProgress();
      const translateY = progress * this.scrollDistance;

      this.track.style.transform = `translate3d(0, ${-translateY}px, 0)`;

      const panelIndex = clamp(
        Math.round(progress * Math.max(this.panels.length - 1, 0)),
        0,
        this.panels.length - 1
      );

      const activePanel = this.panels[panelIndex];
      const stepIndex = activePanel ? parseInt(activePanel.dataset.stepIndex, 10) || 0 : 0;

      this.setActivePanel(panelIndex);
      this.setActiveStep(stepIndex);
    }

    setActivePanel(panelIndex) {
      if (panelIndex === this.activePanel) return;

      this.activePanel = panelIndex;

      this.panels.forEach((panel, index) => {
        panel.classList.toggle('is-active', index === panelIndex);
      });
    }

    setActiveStep(stepIndex) {
      if (stepIndex === this.activeStep) return;

      this.activeStep = stepIndex;

      this.copyItems.forEach((item) => {
        const itemStep = parseInt(item.dataset.stepIndex, 10) || 0;
        item.classList.toggle('is-active', itemStep === stepIndex);
      });
    }
  }

  function initCtVerticalStories() {
    const sections = document.querySelectorAll('.ct-vertical-story');

    sections.forEach((section) => {
      if (section.dataset.ctVerticalStoryReady === 'true') return;

      section.dataset.ctVerticalStoryReady = 'true';
      new CtVerticalStory(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCtVerticalStories);
  } else {
    initCtVerticalStories();
  }
})();